import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Typography, Grid, Paper, List, ListItem, ListItemText, Alert, Switch, Chip, useTheme, Tooltip, FormControlLabel, IconButton, Collapse } from "@mui/material";
import { axiosPrivate } from "../../../api/axios";
import { Icon } from "../../icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const muiColor = (c) => (['primary','secondary','success','error','info','warning'].includes(c) ? c : 'default');

const EditProjectDialog = ({ open, onClose, project }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [projectName, setProjectName] = useState(project?.project_name || "");
  const [projectCode] = useState(project?.project_code || "");

  const [groupUsers, setGroupUsers] = useState([]);
  const [members, setMembers] = useState([]);

  const [systems, setSystems] = useState([]);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [systemAuthById, setSystemAuthById] = useState({});

  const [activeSystemIdx, setActiveSystemIdx] = useState(-1); // -1이면 버튼만 노출
  const [adminLoginId, setAdminLoginId] = useState("");
  const [sysMgmt, setSysMgmt] = useState({ system_types: [], project_systems: [], policies: [], used_counts: [] });

  // section expand states
  const [openInfo, setOpenInfo] = useState(true);
  const [openMembers, setOpenMembers] = useState(false);
  const [openSysManage, setOpenSysManage] = useState(false);
  const [openSysPerm, setOpenSysPerm] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    if (!open || !project) return;
    setLoading(true);
    setError("");
    axiosPrivate
      .post("/project/getProjectAuthData", { project_id: project.project_id })
      .then((res) => {
        const { project: p, group_users, project_members, systems, system_auth, permission_types, admin_login_id } = res.data || {};
        setProjectName(p?.project_name || project.project_name);
        setGroupUsers(group_users || []);
        setMembers(project_members || []);
        setSystems(systems || []);
        setPermissionTypes(permission_types || []);
        setAdminLoginId(admin_login_id || "");
        const map = {};
        (systems || []).forEach((s) => { map[s.project_system_id] = []; });
        (system_auth || []).forEach((sa) => {
          if (!map[sa.project_system_id]) map[sa.project_system_id] = [];
          map[sa.project_system_id].push({ login_id: sa.login_id, permission_id: sa.permission_id });
        });
        setSystemAuthById(map);
        setActiveSystemIdx(-1); // 초기엔 버튼만
        // 시스템 관리 데이터
        return axiosPrivate.post('/project/getSystemManagementData', { project_id: project.project_id });
      })
      .then((res) => {
        if (res?.data) setSysMgmt(res.data);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.message || "권한 데이터 로딩 중 오류가 발생했습니다.");
      })
      .finally(() => setLoading(false));
  }, [open, project]);

  // reset section states when dialog opens
  useEffect(() => {
    if (open) {
      setOpenInfo(true);
      setOpenMembers(false);
      setOpenSysManage(false);
      setOpenSysPerm(false);
    }
  }, [open]);

  const nonAdminUsers = groupUsers.filter((u) => u.login_id !== adminLoginId);

  const getPolicyFor = (type_id) => sysMgmt.policies.find(p => p.system_type_id === type_id) || { is_allowed: 1, system_limit: 0 };
  const getUsedCountFor = (type_id) => (sysMgmt.used_counts.find(u => u.system_type_id === type_id)?.used_count) || 0;
  const getProjectSystemByType = (type_id) => sysMgmt.project_systems.find(ps => ps.system_type_id === type_id);

  const handleCreateSystem = async (type) => {
    const policy = getPolicyFor(type.type_id);
    const used = getUsedCountFor(type.type_id);
    const limit = policy.system_limit || 0;
    if (limit > 0 && used >= limit) {
      alert('생성 가능 수량을 초과했습니다.');
      return;
    }
    if (!window.confirm(`${type.default_name} 시스템을 생성하시겠습니까?`)) return;
    try {
      await axiosPrivate.post('/project/createProjectSystem', { project_id: project.project_id, system_type_id: type.type_id });
      const mgmt = await axiosPrivate.post('/project/getSystemManagementData', { project_id: project.project_id });
      setSysMgmt(mgmt.data);
      // 상단 systems(권한 탭 버튼)도 갱신 필요 시 재조회
      const auth = await axiosPrivate.post('/project/getProjectAuthData', { project_id: project.project_id });
      setSystems(auth.data.systems || []);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '시스템 생성 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteSystem = async (ps) => {
    if (!window.confirm(`'${ps.system_name || (sysMgmt.system_types.find(t=>t.type_id===ps.system_type_id)?.default_name || '')}' 시스템을 삭제하시겠습니까?`)) return;
    try {
      await axiosPrivate.post('/project/deleteProjectSystem', { project_id: project.project_id, project_system_id: ps.project_system_id });
      const mgmt = await axiosPrivate.post('/project/getSystemManagementData', { project_id: project.project_id });
      setSysMgmt(mgmt.data);
      const auth = await axiosPrivate.post('/project/getProjectAuthData', { project_id: project.project_id });
      setSystems(auth.data.systems || []);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '시스템 삭제 중 오류가 발생했습니다.');
    }
  };

  const renderSystemManage = () => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: openSysManage ? 1 : 0 }}>
        <Typography variant="subtitle1">시스템 관리</Typography>
        <IconButton size="small" onClick={() => setOpenSysManage(!openSysManage)} aria-label="toggle-sys-manage">
          {openSysManage ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={openSysManage} unmountOnExit>
        <Grid container spacing={1.5}>
          {sysMgmt.system_types.map((type) => {
            const policy = getPolicyFor(type.type_id);
            const used = getUsedCountFor(type.type_id);
            const limit = Number.isFinite(policy.system_limit) ? Number(policy.system_limit) : 0;
            const atLimit = limit > 0 && used >= limit;
            const projectSystem = getProjectSystemByType(type.type_id);
            const created = !!projectSystem;
            const displayLabel = (created && projectSystem.system_name && projectSystem.system_name.trim()) ? projectSystem.system_name : type.default_name;
            return (
              <Grid item xs={12} sm={6} key={type.type_id}>
                <Box sx={{
                  border: created ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  bgcolor: created ? (theme.palette.mode === 'dark' ? `${theme.palette.primary.darkOpacity}` : `${theme.palette.primary.lightOpacity}`) : 'transparent'
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayLabel}</Typography>
                    <Typography variant="caption" sx={{ color: (limit === 0 || atLimit) ? 'error.main' : 'text.secondary' }}>
                      (사용중: {used} / 사용가능: {limit})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!created ? (
                      <Tooltip title={(limit === 0 || atLimit) ? '생성 가능 수량을 초과했습니다.' : ''} disableHoverListener={!(limit === 0 || atLimit)}>
                        <span>
                          <IconButton size="small" color="primary" disabled={(limit === 0 || atLimit)} onClick={() => handleCreateSystem(type)} aria-label="create-system">
                            <Icon name="plusIcon" style={{ width: 18, height: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" color="error" onClick={() => handleDeleteSystem(projectSystem)} aria-label="delete-system">
                        <Icon name="deleteIcon" style={{ width: 18, height: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Collapse>
    </Paper>
  );

  const handleToggleMember = async (login_id) => {
    const isMember = members.includes(login_id);
    const confirmMsg = isMember ? `${login_id} 계정의 프로젝트 권한을 비활성화할까요?` : `${login_id} 계정에 프로젝트 권한을 활성화할까요?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axiosPrivate.post('/project/toggleProjectMember', {
        project_id: project.project_id,
        login_id,
        enable: !isMember,
      });
      if (isMember) {
        const updated = members.filter((id) => id !== login_id);
        setMembers(updated);
        setSystemAuthById((prev) => {
          const next = {};
          Object.keys(prev).forEach((psid) => {
            next[psid] = (prev[psid] || []).filter((a) => a.login_id !== login_id);
          });
          return next;
        });
      } else {
        setMembers([...members, login_id]);
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const renderEmptyUsers = (text) => (
    <Box sx={{
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: 1,
      mt: 2,
      py: 6,
      textAlign: 'center',
      color: 'text.secondary',
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
    }}>
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const renderMemberSection = () => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: openMembers ? 1 : 0 }}>
        <Typography variant="subtitle1">프로젝트 권한 관리</Typography>
        <IconButton size="small" onClick={() => setOpenMembers(!openMembers)} aria-label="toggle-members">
          {openMembers ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={openMembers} unmountOnExit>
        {nonAdminUsers.length === 0 ? (
          renderEmptyUsers('표시할 계정이 없습니다.')
        ) : (
          <List dense sx={{ width: "100%", maxHeight: 340, overflow: "auto", p: 0 }}>
            {nonAdminUsers.map((u) => {
              const isMember = members.includes(u.login_id);
              const labelId = `switch-member-${u.login_id}`;
              return (
                <ListItem sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mb: 0.5 }} key={u.login_id} secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip size="small" label={isMember ? '활성화' : '비활성화'} color={isMember ? 'success' : 'default'} variant={isMember ? 'filled' : 'outlined'} sx={{ color: isMember ? undefined : 'text.secondary' }} />
                    <Switch edge="end" onChange={() => handleToggleMember(u.login_id)} checked={isMember} inputProps={{ "aria-labelledby": labelId }} />
                  </Box>
                }>
                  <ListItemText id={labelId} primary={u.user_name || u.login_id} secondary={u.login_id} />
                </ListItem>
              );
            })}
          </List>
        )}
      </Collapse>
    </Paper>
  );

  const setSystemPermissionLevel = async (project_system_id, login_id, level) => {
    await axiosPrivate.post('/project/setSystemPermission', {
      project_id: project.project_id,
      project_system_id,
      login_id,
      level, // 0:삭제, 1:조회, 2:편집
    });
  };

  const updateLocalSystemPerm = (project_system_id, login_id, level) => {
    setSystemAuthById((prev) => {
      const arr = prev[project_system_id] ? [...prev[project_system_id]] : [];
      const idx = arr.findIndex((x) => x.login_id === login_id);
      if (level === 0) {
        if (idx !== -1) arr.splice(idx, 1);
      } else {
        if (idx === -1) arr.push({ login_id, permission_id: level });
        else arr[idx] = { login_id, permission_id: level };
      }
      return { ...prev, [project_system_id]: arr };
    });
  };

  const handleToggleView = async (system, login_id, nextOn) => {
    const action = nextOn ? '활성화' : '비활성화';
    if (!window.confirm(`${system.system_name} 시스템의 (조회) 권한을 ${action}하시겠습니까?`)) return;
    try {
      const level = nextOn ? 1 : 0; // on->1, off->0(둘 다 제거)
      await setSystemPermissionLevel(system.project_system_id, login_id, level);
      updateLocalSystemPerm(system.project_system_id, login_id, level);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleToggleEdit = async (system, login_id, nextOn) => {
    const action = nextOn ? '활성화' : '비활성화';
    if (!window.confirm(`${system.system_name} 시스템의 (편집) 권한을 ${action}하시겠습니까?`)) return;
    try {
      const level = nextOn ? 2 : 1; // on->2, off->1(조회만 유지)
      await setSystemPermissionLevel(system.project_system_id, login_id, level);
      updateLocalSystemPerm(system.project_system_id, login_id, level);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const activeSystem = activeSystemIdx >= 0 ? systems[activeSystemIdx] : null;
  const activeSystemId = activeSystem?.project_system_id;
  const activeAssignments = activeSystemId && systemAuthById[activeSystemId] ? systemAuthById[activeSystemId].filter((a) => members.includes(a.login_id)) : [];
  const assignedMap = new Map(activeAssignments.map(a => [a.login_id, a.permission_id || 0]));

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>프로젝트 관리</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: openInfo ? 1 : 0 }}>
              <Typography variant="subtitle1">프로젝트 정보</Typography>
              <IconButton size="small" onClick={() => setOpenInfo(!openInfo)} aria-label="toggle-info">
                {openInfo ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={openInfo} unmountOnExit>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField size="small" label="프로젝트 이름" fullWidth value={projectName} onChange={(e) => setProjectName(e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" label="프로젝트 코드" fullWidth value={projectCode} disabled />
                </Grid>
              </Grid>
            </Collapse>
          </Paper>

          {renderMemberSection()}

          {renderSystemManage()}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: openSysPerm ? 1 : 0 }}>
              <Typography variant="subtitle1">시스템 권한 관리</Typography>
              <IconButton size="small" onClick={() => setOpenSysPerm(!openSysPerm)} aria-label="toggle-sys-perm">
                {openSysPerm ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={openSysPerm} unmountOnExit>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, mb: 1 }}>
                {systems.map((s, idx) => (
                  <Button key={s.project_system_id} variant={activeSystemIdx === idx ? 'contained' : 'outlined'} color={activeSystemIdx === idx ? 'primary' : 'secondary'} size="small" onClick={() => setActiveSystemIdx(idx)} sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.system_name}
                  </Button>
                ))}
              </Box>

              {activeSystem && (
                nonAdminUsers.length === 0 ? (
                  renderEmptyUsers('표시할 계정이 없습니다.')
                ) : (
                  <List dense sx={{ width: "100%", maxHeight: 320, overflow: "auto", p: 0, mt: 2 }}>
                    {nonAdminUsers.map((u) => {
                      const login_id = u.login_id;
                      const isProjectMember = members.includes(login_id);
                      const currentLevel = assignedMap.get(login_id) || 0;
                      const hasView = currentLevel >= 1;
                      const hasEdit = currentLevel >= 2;
                      const viewDisabled = !isProjectMember;
                      const editDisabled = !hasView || !isProjectMember;
                      return (
                        <ListItem sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mb: 0.5 }} key={login_id} secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip size="small" label={hasEdit ? '편집' : (hasView ? '조회' : '비활성화')} color={hasEdit ? 'success' : (hasView ? 'info' : 'default')} variant={hasView ? 'filled' : 'outlined'} sx={{ color: hasView ? undefined : 'text.secondary' }} />
                            <Tooltip title={viewDisabled ? '프로젝트 권한이 없습니다.' : ''} disableHoverListener={!viewDisabled}>
                              <span>
                                <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={hasView} disabled={viewDisabled} onChange={(e) => handleToggleView(activeSystem, login_id, e.target.checked)} />} label={<Typography variant="caption">조회</Typography>} />
                              </span>
                            </Tooltip>
                            <Tooltip title={editDisabled ? (!isProjectMember ? '프로젝트 권한이 없습니다.' : '조회 권한이 필요합니다.') : ''} disableHoverListener={!editDisabled}>
                              <span>
                                <FormControlLabel control={<Switch size="small" checked={hasEdit} disabled={editDisabled} onChange={(e) => handleToggleEdit(activeSystem, login_id, e.target.checked)} />} label={<Typography variant="caption">편집</Typography>} />
                              </span>
                            </Tooltip>
                          </Box>
                        }>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
                            <ListItemText primary={u.user_name || login_id} secondary={login_id} />
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )
              )}
            </Collapse>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={async () => { try { setLoading(true); setError(""); if (projectName && projectName !== project.project_name) { await axiosPrivate.post("/project/updateProjectName", { project_id: project.project_id, project_name: projectName }); } onClose(true); } catch (e) { console.error(e); alert(e.response?.data?.message || '저장 중 오류가 발생했습니다.'); } finally { setLoading(false); } }} variant="contained" disabled={loading}>
          저장
        </Button>
        <Button onClick={() => onClose(false)}>취소</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectDialog;
